import Image from 'next/image'
import React, { useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { VideoProps } from '~types'
import styles from './Video.module.scss'
import { useIsIntersecting } from 'utils/hooks/useIsIntersecting'
import { NEXT_IMAGE_QUALITY } from '~constants'
import { useWindowSize } from 'utils/hooks'
import { useRouter } from 'next/router'
import { fetchVimeoVideo, getVimeoPictureByResolution, getVimeoVideoByRendition } from 'lib/vimeo'
import PlayIcon from '../../../public/assets/svg/play.svg'
import PauseIcon from '../../../public/assets/svg/pause.svg'
import MuteIcon from '../../../public/assets/svg/mute.svg'
import UnmuteIcon from '../../../public/assets/svg/unmute.svg'

enum VideoPlaybackMode {
  SUCCESS = 'success',
  FAILED = 'failed',
  LOADING = 'loading'
}

type VimeoData = {
  url?: string | null
  poster?: {
    url: string
    altText: string
  }
}

const Video = ({
  autoPlay = true,
  backgroundVideoXl,
  backgroundVideoMd,
  backgroundVideoSm,
  className,
  posterImageXl,
  posterImageMd,
  posterImageSm,
  videoLoop,
  externalVideoRef,
  preload = 'none',
  handlePlaybackFailed,
  isChildOfHeroCarousel = false,
  isPaused,
  vimeoIdXl,
  vimeoIdSm,
  allowPause,
  allowVolume
}: VideoProps) => {
  const router = useRouter()
  const internalVideoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const backgroundVideoRef = externalVideoRef || internalVideoRef
  const [videoPlaybackMode, setVideoPlaybackMode] = useState<string>(VideoPlaybackMode.LOADING)
  const [vimeoData, setVimeoData] = useState<{
    desktop?: VimeoData
    mobile?: VimeoData
  }>({})
  const [isVimeoDataLoaded, setIsVimeoDataLoaded] = useState<boolean>(false)
  const [isPlaying, setIsPlaying] = useState(true)
  const [isMuted, setIsMuted] = useState(true)
  const [hasUserPaused, setHasUserPaused] = useState(false)

  const { isBreakpoint: isDesktop } = useWindowSize('md', '>')
  const { isBreakpoint: isTabletAndUp } = useWindowSize('sm', '>=')

  const fallbackImage = vimeoData.desktop?.poster?.url
    ? { url: vimeoData.desktop.poster.url }
    : vimeoData.mobile?.poster?.url
    ? { url: vimeoData.mobile.poster.url }
    : posterImageXl ?? posterImageMd ?? posterImageSm

  const fallbackVideo = vimeoData.desktop?.url
    ? { url: vimeoData.desktop.url }
    : vimeoData.mobile?.url
    ? { url: vimeoData.mobile.url }
    : backgroundVideoXl ?? backgroundVideoMd ?? backgroundVideoSm

  const currentVimeoData = isDesktop ? vimeoData.desktop : vimeoData.mobile

  const image = currentVimeoData?.poster?.url
    ? { url: currentVimeoData?.poster.url }
    : (isDesktop ? posterImageXl : isTabletAndUp ? posterImageMd : posterImageSm) ?? fallbackImage

  const video = currentVimeoData?.url
    ? { url: currentVimeoData.url }
    : (isDesktop ? backgroundVideoXl : isTabletAndUp ? backgroundVideoMd : backgroundVideoSm) ?? fallbackVideo

  const isElementIntersecting = useIsIntersecting({
    ref: containerRef,
    observerOptions: {
      root: null,
      rootMargin: '0px',
      threshold: 0.01
    }
  })
  const videoKey = `video-${video?.url}`

  const playVideo = async () => {
    if (!backgroundVideoRef.current) return
    try {
      await backgroundVideoRef.current.play()
      backgroundVideoRef.current.poster = ''
      setVideoPlaybackMode(VideoPlaybackMode.SUCCESS)
      handlePlaybackFailed?.(false)
      setIsPlaying(true)
    } catch (error) {
      console.warn(`Failed to Reproduce Video`)
      setVideoPlaybackMode(VideoPlaybackMode.FAILED)
      handlePlaybackFailed?.(true)
    }
  }

  const fetchVimeoData = async () => {
    if (!vimeoIdXl && !vimeoIdSm) {
      setIsVimeoDataLoaded(true)
      return
    }
    setVideoPlaybackMode(VideoPlaybackMode.LOADING)

    try {
      const results = await Promise.allSettled([
        vimeoIdXl ? fetchSingleVimeoVideo(vimeoIdXl) : Promise.resolve(null),
        vimeoIdSm ? fetchSingleVimeoVideo(vimeoIdSm) : Promise.resolve(null)
      ])

      const newVimeoData: {
        desktop?: VimeoData
        mobile?: VimeoData
      } = {}

      // Process desktop result
      if (results[0].status === 'fulfilled' && results[0].value) {
        newVimeoData.desktop = results[0].value
      } else if (vimeoIdXl) {
        console.warn(`Failed to fetch desktop Vimeo video for ID: ${vimeoIdXl}`)
      }
      // Process mobile result
      if (results[1].status === 'fulfilled' && results[1].value) {
        newVimeoData.mobile = results[1].value
      } else if (vimeoIdSm) {
        console.warn(`Failed to fetch mobile Vimeo video for ID: ${vimeoIdSm}`)
      }

      setVimeoData(newVimeoData)
      setIsVimeoDataLoaded(true)
    } catch (error) {
      console.warn('Error fetching Vimeo videos:', error)
      setIsVimeoDataLoaded(true) // Continue with fallback options
    }
  }

  const fetchSingleVimeoVideo = async (vimeoId: string): Promise<VimeoData> => {
    const vimeoData = await fetchVimeoVideo(vimeoId)
    const url = getVimeoVideoByRendition('1080p', vimeoData)
    const posterUrl = getVimeoPictureByResolution('1920x1080', vimeoData)

    return {
      url,
      poster: posterUrl ? { url: posterUrl, altText: 'vimeo-video' } : undefined
    }
  }

  useEffect(() => {
    fetchVimeoData()
  }, [vimeoIdXl, vimeoIdSm])

  useEffect(() => {
    const videoElement = backgroundVideoRef.current
    if (!videoElement || !isVimeoDataLoaded) return

    if (isElementIntersecting && !isPaused) {
      if (!hasUserPaused) {
        playVideo()
      }

      if (!isMuted) {
        videoElement.muted = false
      }
    } else {
      if (!isMuted) {
        videoElement.muted = true
      }
    }

    return () => {
      if (videoElement) {
        if (router.asPath !== window.location.pathname) {
          videoElement.pause()
          videoElement.currentTime = 0
        }
      }
    }
  }, [isElementIntersecting, backgroundVideoRef, isPaused, router.asPath, isVimeoDataLoaded, isMuted, hasUserPaused])

  const togglePlay = () => {
    const videoEl = backgroundVideoRef.current
    if (!videoEl) return
    if (isPlaying) {
      videoEl.pause()
      setHasUserPaused(true)
    } else {
      videoEl.play()
      setHasUserPaused(false)
    }
    setIsPlaying(!isPlaying)
  }

  const toggleMute = () => {
    const videoEl = backgroundVideoRef.current
    if (!videoEl) return
    videoEl.muted = !isMuted
    setIsMuted(!isMuted)
  }

  if (!image && !video) return null

  return (
    <div className={cx(styles['full-screen-video-container'], className)} ref={containerRef}>
      <div
        className={cx(
          styles['poster-container'],
          videoPlaybackMode === VideoPlaybackMode.SUCCESS ? styles.hidden : styles.visible
        )}
      >
        {image && (
          <Image
            alt={image.altText ?? ''}
            src={image.url}
            width='0'
            height='0'
            sizes='100vw'
            quality={NEXT_IMAGE_QUALITY}
          />
        )}
      </div>

      <video
        key={videoKey}
        autoPlay={autoPlay}
        loop={videoLoop}
        ref={backgroundVideoRef}
        muted
        playsInline
        poster={videoPlaybackMode === VideoPlaybackMode.LOADING ? image?.url : ''}
        disableRemotePlayback
        preload={preload}
        className={cx(videoPlaybackMode === VideoPlaybackMode.SUCCESS ? styles.visible : styles.hidden)}
      >
        {video && <source src={video.url} type='video/mp4' />}
      </video>

      <div className={cx([styles.controls, isChildOfHeroCarousel && styles.isChildOfHeroCarousel])}>
        {allowPause && (
          <button
            onClick={togglePlay}
            aria-label={isPlaying ? 'Pause video' : 'Play video'}
            className={styles.controlButton}
          >
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>
        )}

        {allowVolume && (
          <button onClick={toggleMute} aria-label={isMuted ? 'Unmute' : 'Mute'} className={styles.controlButton}>
            {isMuted ? <MuteIcon /> : <UnmuteIcon />}
          </button>
        )}
      </div>
    </div>
  )
}

export default Video
